import type { IFilter } from '@models/Filter'

import { apiClient } from '../../../gateways/api'
import type { AssignedBijlagenSearch } from '../types/AssignedBijlagenSearch'

export { initialState, createFilters, searchAssignedBijlagen }

const initialState: AssignedBijlagenSearch = {
  data: { count: 0, skip: 0, limit: 25, items: [] },
  query: {
    filters: {
      gebied: [],
      documentsoort: [],
      assignedTo: [],
      fromWeek: '',
      toWeek: '',
      labels: [],
      myList: false,
      startUitvoeringWeekTot: '',
      startUitvoeringWeekVan: '',
      gemeente: [],
      processtatus: [],
      verantwoordelijke: null,
      contactpersoon: null,
      status: null,
      typeWerk: [],
      due: true,
      kpi: '',
    },
    sort: { sortBy: 'created.date', sortDirection: 'desc' },
    page: { skip: 0, limit: 25 },
  },
}

const createFilters = ({
  gebied,
  documentsoort,
  assignedTo,
  toWeek,
  fromWeek,
  labels,
  myList,
  startUitvoeringWeekVan,
  startUitvoeringWeekTot,
  gemeente,
  processtatus,
  verantwoordelijke,
  contactpersoon,
  status,
  typeWerk,
  due,
  kpi,
}) => {
  const filters: IFilter[] = []

  if (documentsoort && documentsoort.length > 0)
    filters.push({
      name: 'documentsoort',
      value: documentsoort,
      operator: 'in',
    })
  if (gebied && gebied.length > 0)
    filters.push({
      name: 'entity.gebiedscode',
      value: gebied.map(({ code }) => code),
      operator: 'in',
    })
  if (assignedTo && assignedTo.length > 0)
    filters.push({
      name: 'assignedTo',
      value: assignedTo.map(({ label }) => label),
      operator: 'in',
    })
  if (fromWeek)
    filters.push({ name: 'dueWeek', value: fromWeek, operator: 'gte' })
  if (toWeek) filters.push({ name: 'dueWeek', value: toWeek, operator: 'lte' })

  if (startUitvoeringWeekVan)
    filters.push({
      name: 'searchValues.startUitvoeringWeek',
      value: startUitvoeringWeekVan,
      operator: 'gte',
    })
  if (startUitvoeringWeekTot)
    filters.push({
      name: 'searchValues.startUitvoeringWeek',
      value: startUitvoeringWeekTot,
      operator: 'lte',
    })
  if (gemeente.length > 0)
    filters.push({
      name: 'searchValues.gemeente',
      value: gemeente,
      operator: 'in',
    })
  if (processtatus && processtatus.length > 0) {
    filters.push({
      name: 'processtatus',
      value: processtatus,
      operator: 'in',
    })
    filters.push({
      name: 'searchValues.statusText',
      value: processtatus,
      operator: 'in',
    })
  }

  if (verantwoordelijke) {
    filters.push({
      name: 'searchValues.verantwoordelijken',
      value: verantwoordelijke.code,
      operator: 'eq',
    })
  }

  if (contactpersoon)
    filters.push({
      name: 'contactpersoon',
      operator: 'eq',
      value: contactpersoon.userid,
    })

  filters.push({
    name: 'isActive',
    value: true,
    operator: 'eq',
  })

  if (labels && labels.length > 0)
    filters.push({
      name: 'labels',
      value: labels.map(({ code }) => code),
      operator: 'in',
    })

  filters.push({
    name: 'entity.projecttype',
    value: ['Hoofdleiding'],
    operator: 'in',
  })

  if (status) {
    filters.push({ name: 'status', value: [status.code], operator: 'in' })
  } else {
    filters.push({
      name: 'status',
      value: ['assigned', 'uploaded', 'review'],
      operator: 'in',
    })
  }

  if (myList) {
    filters.push({ name: 'my-list', value: true, operator: 'eq' })
  }

  if (Array.isArray(typeWerk) && typeWerk.length > 0) {
    filters.push({
      name: 'searchValues.typeWerk',
      operator: 'in',
      value: typeWerk,
    })
  }

  if (due) {
    filters.push({
      name: 'due',
      value: true,
      operator: 'eq',
    })
  }

  if (kpi) {
    filters.push({ name: 'kpi', value: kpi, operator: 'eq' })
  }

  return filters
}

const searchAssignedBijlagen = async ({
  query,
}: {
  query: AssignedBijlagenSearch['query']
}): Promise<AssignedBijlagenSearch['data']> => {
  const { filters, page, sort } = query

  const { data } = await apiClient({
    url: '/rest/bijlageassigned/search',
    method: 'POST',
    data: { filters: createFilters(filters), ...page, ...sort },
  })

  return data
}
